/**
 * REV-21057 — Contract types for the SharePoint "Ordner hinzufügen" pure view
 * (aumico-integration-sharepoint-folders).
 *
 * These are the only shapes the presentational component knows about. It owns NO
 * store, NO HTTP and NO Microsoft/OAuth logic — the parent computes the connection
 * state and the selected folders and passes them in as @Inputs.
 */

/**
 * Lifecycle of the document source (Microsoft 365) connection, as seen by the view.
 *
 * `awaiting-consent` is a PRE-REQUEST, in-app state (REV-21057): the user clicked
 * "Mit Microsoft 365 verbinden" but has not approved yet, so aumico renders its own
 * consent card before any Microsoft popup is opened. It is deliberately NOT a server
 * state — the parent owns it as local UI state.
 */
export type FolderSourceStatus =
    | 'disconnected'
    | 'awaiting-consent'
    | 'connecting'
    | 'connected'
    | 'error';

/**
 * One line in the "Angeforderte Berechtigung" list of the in-app consent card.
 *
 * Because the tenant may have granted admin consent (Microsoft then SKIPS its own
 * consent screen), this card is the only place the user is told what aumico requests
 * — so every requested scope gets its own line.
 */
export interface RequestedPermission {
    /** i18next key for the permission title (e.g. "Dateien lesen"). */
    labelKey: string;
    /** Optional i18next key for the reassurance sentence under the title. */
    descriptionKey?: string;
}

/** One selectable document-source tile (SharePoint / Google Drive / manueller Upload). */
export interface DocumentSourceProvider {
    id: 'sharepoint' | 'google-drive' | 'manual-upload';
    /** i18next key for the tile title. */
    labelKey: string;
    /** i18next key for the short tile description. */
    descriptionKey: string;
    /** Lucide icon name (kebab-case, e.g. "cloud"). */
    icon: string;
    /** Disabled tiles are shown but not clickable. */
    enabled: boolean;
}

/** State of the SharePoint / Microsoft 365 connection. */
export interface SharepointConnection {
    status: FolderSourceStatus;
    /** The connected Microsoft 365 account (shown in the "Verbunden als …" banner). */
    account?: string;
    /** i18next key for a human-readable error, shown when status === 'error'. */
    errorMessageKey?: string;
}

/** A folder the user has picked from SharePoint / OneDrive for the closing. */
export interface SelectedClosingFolder {
    id: string;
    path: string;
    source: 'sharepoint' | 'onedrive';
    fileCount?: number;
    includeSubfolders: boolean;
    account: string;
}
